'use client';

import { changeStaff, logout } from '@/helpers/accountHelper';
import { PORTAL_USE_TYPE } from '@/types/types';
import { signIn, useSession } from 'next-auth/react';
import { useState, MouseEvent } from 'react';
import { MuiButton } from '@/components/button/MuiButton';
import { MuiAvatar } from '@/components/avatar/MuiAvatar';
import { Avatar, Menu, MenuItem, Typography } from '@mui/material';
import { usePageLoader } from '@/hooks/usePageLoader';
import { MuiChip } from '../chip/MuiChip';
import { Home, HospitalBuilding, CommentTextOutline } from 'mdi-material-ui';
import {
  getPortalStaffDisplayName,
  getRequestUserDisplayName,
} from '@/helpers/displayNameHelper';

export function SelectAccountMenu() {
  const { data: session } = useSession();
  const { setLoading } = usePageLoader();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onChangeUser = async ({
    useType,
    portalStaff,
    requestUser,
  }: {
    useType: PORTAL_USE_TYPE;
    portalStaff?: {
      portalOfficeStaffId: number;
      portalOfficeId: number;
      ibowOfficeId: number;
      ibowStaffId: number;
    };
    requestUser?: {
      portalRequestUserId: number;
      organizationId: number;
      ibowStaffId: number;
    };
  }) => {
    setLoading(true);
    const res = await changeStaff({
      portalStaff,
      requestUser,
    });
    if (!res?.ok) {
      window.location.href = `/signin?use_type=${useType}`;
      return;
    }
    const result = await signIn('portal-credentials', {
      use_type: useType,
      redirect: false,
    });
    if (!result?.error) {
      window.location.href = useType === 1 ? '/admin' : '/app';
    } else {
      window.location.href = `/signin?use_type=${useType}`;
    }
    setLoading(false);
  };

  const portalStaff = session?.user?.portal_staff;
  const requestUser = session?.user?.request_user;
  const portalStaffs = session?.user?.portal_staffs.filter(
    (staff) => staff.id !== session.user?.portal_staff?.id,
  );
  const requestUsers = session?.user?.request_users.filter(
    (user) => user.id !== session?.user?.request_user?.id,
  );

  const user = portalStaff || requestUser || null;
  const displayname = portalStaff
    ? getPortalStaffDisplayName(portalStaff)
    : requestUser
      ? getRequestUserDisplayName(requestUser)
      : '';
  const organization =
    portalStaff?.portal_office?.name || requestUser?.organization?.name || '';
  const params = new URLSearchParams();
  if (user) {
    params.append('systemid', user.system_id || '');
    params.append('ibowid', `${session?.user?.ewell_user?.user_id || ''}`);
    params.append('side', !!portalStaff ? '掲載側' : '紹介側');
    params.append('familyname', session?.user?.ewell_user?.family_name || '');
    params.append('firstname', session?.user?.ewell_user?.first_name || '');
    params.append('mail', session?.user?.ewell_user?.mail_address || '');
    params.append('phone', session?.user?.ewell_user?.tel_no || '');
    params.append('organization', organization || '');
    params.append('displayname', displayname || '');
  }

  const contactURL = `${
    process.env.NEXT_PUBLIC_CONTACT_FORM_URL || ''
  }?${params.toString()}`;
  const guideURL = 'https://guide.carelogood.jp/';

  return (
    <>
      <MuiButton
        id="header-profile-icon"
        variant="text"
        onClick={handleClick}
        sx={{ padding: 0 }}
      >
        <MuiAvatar sx={{ width: '36px', height: '36px' }} />
      </MuiButton>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'header-profile-icon',
        }}
      >
        <MenuItem
          onClick={() =>
            (window.location.href =
              process.env.NEXT_PUBLIC_IBOW_MYPAGE_URL || '')
          }
          sx={{ columnGap: 1 }}
        >
          <Avatar sx={{ background: 'none' }}>
            <Home sx={{ color: '#0288d1', fontSize: '2.5rem' }} />
          </Avatar>
          <Typography variant="JP/Body/m/Medium/Tight" color="#0288d1">
            iBowマイページ
          </Typography>
        </MenuItem>
        {!!portalStaff && (
          <MenuItem
            sx={{
              ':hover': { bgcolor: 'inherit' },
              cursor: 'default',
              py: 1,
              columnGap: 1,
            }}
          >
            <Avatar sx={{ background: '#33BEB9' }}>
              <HospitalBuilding sx={{ color: '#FFFFFF' }} />
            </Avatar>
            <Typography
              variant="JP/Body/m/Medium/Tight"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {portalStaff.portal_office?.name}(掲載)
              <MuiChip size="small" label="ログイン中" />
            </Typography>
          </MenuItem>
        )}
        {!!requestUser && (
          <MenuItem
            sx={{
              ':hover': { bgcolor: 'inherit' },
              cursor: 'default',
              py: 1,
              columnGap: 1,
            }}
          >
            <Typography
              variant="JP/Body/m/Medium/Tight"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Avatar sx={{ background: '#33BEB9' }}>
                <HospitalBuilding sx={{ color: '#FFFFFF' }} />
              </Avatar>
              {requestUser.organization?.name}(紹介)
              <MuiChip size="small" label="ログイン中" />
            </Typography>
          </MenuItem>
        )}
        {portalStaffs?.map((staff) => {
          if (!staff.id) return <></>;
          return (
            <MenuItem
              onClick={() =>
                onChangeUser({
                  useType: PORTAL_USE_TYPE.PORTAL_STAFF,
                  portalStaff: {
                    portalOfficeStaffId: staff.id as number,
                    portalOfficeId: staff.portal_office_id as number,
                    ibowOfficeId: staff.ibow_office_id as number,
                    ibowStaffId: staff.ibow_staff_id as number,
                  },
                })
              }
              key={`staff-${staff.id}`}
              sx={{ py: 1, columnGap: 1 }}
            >
              <Avatar sx={{ background: '#33BEB9' }}>
                <HospitalBuilding sx={{ color: '#FFFFFF' }} />
              </Avatar>
              <Typography variant="JP/Body/m/Medium/Tight">
                {staff.portal_office_name}(掲載)
              </Typography>
            </MenuItem>
          );
        })}
        {requestUsers?.map((user) => {
          if (!user.id) return <></>;
          return (
            <MenuItem
              onClick={() =>
                onChangeUser({
                  useType: PORTAL_USE_TYPE.REQUEST_USER,
                  requestUser: {
                    portalRequestUserId: user.id as number,
                    organizationId: user.organization_id as number,
                    ibowStaffId: user.ibow_staff_id as number,
                  },
                })
              }
              key={`user-${user.id}`}
              sx={{ py: 1, columnGap: 1 }}
            >
              <Avatar sx={{ background: '#33BEB9' }}>
                <HospitalBuilding sx={{ color: '#FFFFFF' }} />
              </Avatar>
              <Typography variant="JP/Body/m/Medium/Tight">
                {user.organization_name}(紹介)
              </Typography>
            </MenuItem>
          );
        })}
        <MenuItem
          component="a"
          href={contactURL}
          target="_blank"
          sx={{ columnGap: 1, color: '#FFD600' }}
        >
          <Avatar sx={{ background: 'none' }}>
            <CommentTextOutline sx={{ color: '#C4C4C4', fontSize: '2.5rem' }} />
          </Avatar>
          <Typography variant="JP/Body/m/Medium/Tight">問い合わせ</Typography>
        </MenuItem>

        <MenuItem
          component="a"
          href={guideURL}
          target="_blank"
          sx={{ columnGap: 1, color: '#FFD600' }}
        >
          <Avatar sx={{ background: 'none' }}>
            <CommentTextOutline sx={{ color: '#C4C4C4', fontSize: '2.5rem' }} />
          </Avatar>
          <Typography variant="JP/Body/m/Medium/Tight">ガイド/FAQ</Typography>
        </MenuItem>

        <MenuItem
          onClick={() => logout(session?.user?.use_type)}
          sx={{ color: '#FF5C00', columnGap: 1 }}
        >
          <Avatar sx={{ background: 'none' }} />
          <Typography variant="JP/Body/m/Medium/Tight">ログアウト</Typography>
        </MenuItem>
      </Menu>
    </>
  );
}
